import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Phone } from "lucide-react";
import { useBusinessProfile } from "@/lib/useBusinessProfile";
import wordmark from "@/assets/fenny-wordmark-butter.svg";
import { FooterCallback } from "@/components/site/FooterCallback";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { useLocalizedHref } from "@/i18n/useLocalizedHref";
import { CONTACT } from "@/content/contact";
import { openConsentSettings } from "@/components/consent/ConsentBanner";


export function SiteFooter() {
  const { t } = useTranslation();
  const loc = useLocalizedHref();
  const year = new Date().getFullYear();
  const business = useBusinessProfile();
  return (
    <footer className="border-t border-white/10 bg-background">
      <div className="container-wide py-16">
        <section
          aria-labelledby="footer-cta-heading"
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-surface/80 via-background to-surface/60 p-8 md:p-12"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-mint/10 blur-3xl" />
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-mint">{t("footer.ctaEyebrow")}</div>
              <h2 id="footer-cta-heading" className="mt-3 text-balance text-3xl font-semibold leading-tight md:text-5xl">
                {t("footer.ctaTitleA")} <em className="font-serif italic text-butter">{t("footer.ctaTitleB")}</em>
              </h2>
              <p className="mt-4 max-w-md text-sm text-muted-foreground md:text-base">{t("footer.ctaBody")}</p>
              <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5"><Check /> {t("footer.bullet24h")}</li>
                <li className="flex items-center gap-1.5"><Check /> {t("footer.bulletNoPitch")}</li>
                <li className="flex items-center gap-1.5"><Check /> {t("footer.bulletFixed")}</li>
              </ul>
            </div>

            <div>
              <FooterCallback />
              <p className="mt-3 text-xs text-muted-foreground">
                {t("footer.prefersEmail")}{" "}
                <a className="text-foreground underline-offset-4 hover:text-mint hover:underline" href={CONTACT.emailMailto}>{CONTACT.email}</a>{" "}·{" "}
                <Link to={loc("/kontakt") as string} className="hover:text-mint">{t("footer.fullForm")} →</Link>
              </p>
            </div>
          </div>
        </section>

        <div className="mt-16 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <img src={wordmark} alt="Fenny" loading="lazy" decoding="async" className="h-6 w-auto" />
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">{t("footer.tagline")}</p>

            <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-surface/60 px-4 py-3">
              <span aria-hidden className="text-lg">🇸🇪</span>
              <div className="leading-tight">
                <div className="text-sm font-semibold">{t("footer.builtInSweden")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("footer.builtInSwedenBody")}{" "}
                  <a
                    href="https://norrhavet.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-4 hover:text-mint hover:underline"
                  >
                    norrhavet.com
                  </a>
                </div>
              </div>
            </div>

            {/* NAP — samma uppgifter som i strukturerad data och kataloger. */}
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-mint" /><a className="hover:text-mint" href={CONTACT.emailMailto}>{CONTACT.email}</a></li>
              {business.phone ? (
                <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-mint" /><a className="hover:text-mint" href={`tel:${business.phoneHref}`}>{business.phone}</a></li>
              ) : null}
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                <span>
                  {business.streetAddress ? (
                    <>
                      {business.streetAddress}
                      {business.postalCode ? `, ${business.postalCode}` : ""} {business.city}
                      <br />
                    </>
                  ) : null}
                  {CONTACT.citiesLong}
                </span>
              </li>
            </ul>

          </div>

          <FooterCol className="md:col-span-2" title={t("footer.colServices")} items={[
            { label: t("footer.links.pulse"), to: loc("/pulse") },
            { label: t("footer.links.prime"), to: loc("/prime") },
            { label: t("footer.links.pricing"), to: loc("/priser") },
            { label: t("footer.links.reklamfilm"), to: loc("/reklamfilm") },
            { label: t("footer.links.foretagsfilm"), to: loc("/foretagsfilm") },
            { label: t("footer.links.employerBranding"), to: loc("/employer-branding-film") },
            
            { label: t("footer.links.allFormats"), to: loc("/tjanster") },
          ]} />

          <FooterCol className="md:col-span-2" title={t("footer.colForWhom")} items={[
            { label: t("footer.links.saas"), to: loc("/branscher/saas") },
            { label: t("footer.links.industri"), to: loc("/branscher/industri") },
            { label: t("footer.links.ehandel"), to: loc("/branscher/e-handel") },
            { label: t("footer.links.fastighet"), to: loc("/branscher/fastighet") },
            { label: t("footer.links.offentlig"), to: loc("/branscher/offentlig-sektor") },
            
            { label: t("footer.links.allIndustries"), to: loc("/tjanster") },
          ]} />

          <FooterCol className="md:col-span-2" title={t("footer.colFenny")} items={[
            { label: t("footer.links.about"), to: loc("/om-fenny") },
            { label: t("footer.links.cases"), to: loc("/case") },
            { label: t("footer.links.careers"), to: loc("/karriar") },
            { label: t("footer.links.knowledge"), to: loc("/kunskap") },
            { label: t("footer.links.faq"), to: loc("/faq") },
            { label: t("footer.links.contact"), to: loc("/kontakt") },
          ]} />

          <FooterCol className="md:col-span-2" title={t("footer.colCompare")} items={[
            { label: t("footer.links.vsAgency"), to: loc("/jamfor/inhouse-vs-filmbyra") },
            { label: t("footer.links.vsFreelance"), to: loc("/jamfor/filmbyra-vs-frilansare") },
            { label: t("footer.links.subVsProject"), to: loc("/jamfor/filmabonnemang-vs-projektfilm") },
            { label: t("footer.links.pulseVsPrime"), to: loc("/jamfor/pulse-vs-prime") },
          ]} />
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span>© {year} <Link to="/" className="hover:text-mint">Fenny</Link> · {t("footer.rightsPrefix")}</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to={loc("/integritet") as string} className="hover:text-mint">{t("footer.privacy")}</Link>
            <Link to={loc("/faq") as string} className="hover:text-mint">{t("footer.faq")}</Link>
            <Link to="/redaktionell-policy" className="hover:text-mint">Redaktionell policy</Link>
            <Link to="/alla-sidor" className="hover:text-mint">Alla sidor</Link>

            <button type="button" onClick={openConsentSettings} className="hover:text-mint">
              Cookieinställningar
            </button>


          </div>
          <div>
            <LanguageSwitcher variant="ghost" />
          </div>
        </div>
      </div>
    </footer>
  );
}

function Check() {
  return (
    <span className="grid h-4 w-4 place-items-center rounded-full bg-mint/15 text-mint">
      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 6.5L5 9.5L10 3.5" />
      </svg>
    </span>
  );
}


function FooterCol({ title, items, className }: { title: string; items: { label: string; to?: string; href?: string }[]; className?: string }) {
  return (
    <div className={className}>
      <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">{title}</div>
      <ul className="space-y-2 text-sm">
        {items.map((i) =>
          i.to ? (
            <li key={i.label}><Link to={i.to as string} className="hover:text-mint">{i.label}</Link></li>
          ) : (
            <li key={i.label}><a href={i.href} className="hover:text-mint">{i.label}</a></li>
          ),
        )}
      </ul>
    </div>
  );
}
